import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ArrowLeft, ArrowRight, CheckCircle2, Lock, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { WIZARD_STEPS, STATUS_LABEL, BRAND } from "@/lib/constants";
import {
  StepBienvenida, StepDatos, StepDiseno, StepTerminaciones, StepUpgrades, StepPedido,
} from "@/components/wizard/WizardSteps";
import { BrandFooter } from "@/components/Brand";

const STEP_COMPONENTS = {
  bienvenida: StepBienvenida,
  datos: StepDatos,
  diseno: StepDiseno,
  terminaciones: StepTerminaciones,
  upgrades: StepUpgrades,
  pedido: StepPedido,
};

const LOCKED = new Set(["enviado_para_revisar", "aprobado"]);

function navClasses(active, done) {
  if (active) return "border-[#FF5A00] bg-[#FF5A00]/5 text-white";
  if (done) return "border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-900";
  return "border-zinc-900 text-zinc-500 hover:text-zinc-300";
}
function badgeClasses(active, done) {
  if (active) return "bg-[#FF5A00] text-white";
  if (done) return "bg-zinc-800 text-zinc-300";
  return "bg-zinc-900 text-zinc-600";
}

export default function ExpositorWizard() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [stand, setStand] = useState(null);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/expositor/stand/${code}`);
      if (data.full_equipado === false && sessionStorage.getItem(`fe_optin_${data.stand_number}`) !== "1") {
        navigate(`/servicios/${data.stand_number}`, { replace: true });
        return;
      }
      setStand(data);
    } catch {
      toast.error("Stand no encontrado");
      navigate("/expositor");
    } finally {
      setLoading(false);
    }
  }, [code, navigate]);

  useEffect(() => { load(); }, [load]);

  const progress = useMemo(() => (step / WIZARD_STEPS.length) * 100, [step]);
  const stepDef = WIZARD_STEPS.find((s) => s.id === step);
  const StepComp = stepDef ? STEP_COMPONENTS[stepDef.key] : null;
  const adminEditMode = (() => {
    try { return !!sessionStorage.getItem("am_admin_edit_mode"); } catch { return false; }
  })();
  const locked = stand && LOCKED.has(stand.status) && !stand.reopened && !adminEditMode;

  if (loading) return <div className="min-h-screen bg-[#09090B] flex items-center justify-center text-zinc-400"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando...</div>;
  if (!stand) return null;

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <header className="sticky top-0 z-20 backdrop-blur-xl bg-zinc-950/80 border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <img src={BRAND.LOGO_AM} alt="Argentina Mining Norte 2026" className="h-9 w-auto shrink-0" />
            <div className="hidden sm:block h-7 w-px bg-zinc-800" />
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Espacio {stand.stand_number} · {stand.stand_type}</div>
              <div className="text-sm font-semibold truncate">{stand.company}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {(() => {
              try {
                const raw = sessionStorage.getItem("mis_espacios");
                if (!raw) return null;
                const data = JSON.parse(raw);
                if (!data?.groups || data.groups.length < 2) return null;
                return (
                  <Button
                    data-testid="mis-espacios-btn"
                    onClick={() => navigate("/mis-espacios")}
                    variant="outline"
                    className="hidden sm:inline-flex border-[#FF5A00]/40 text-[#FF5A00] hover:bg-[#FF5A00]/10 rounded-sm h-8 px-3 text-xs"
                  >
                    <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Mis espacios ({data.groups.length})
                  </Button>
                );
              } catch { return null; }
            })()}
            {stand.full_equipado === false && (
              <Button
                data-testid="volver-servicios-btn"
                onClick={() => {
                  sessionStorage.removeItem(`fe_optin_${stand.stand_number}`);
                  navigate(`/servicios/${stand.stand_number}`);
                }}
                variant="outline"
                className="hidden sm:inline-flex border-zinc-800 hover:bg-zinc-900 rounded-sm h-8 px-3 text-xs"
              >
                <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Servicios Oficiales
              </Button>
            )}
            <Badge variant="outline" className="border-zinc-700 text-zinc-300 rounded-sm text-[10px]" data-testid="stand-status-badge">
              {STATUS_LABEL[stand.status]}
            </Badge>
            {locked && (
              <Badge className="bg-amber-500/10 text-amber-300 border border-amber-500/30 rounded-sm gap-1">
                <Lock className="w-3 h-3" /> Bloqueado
              </Badge>
            )}
            <img src={BRAND.LOGO_SO} alt="Servicios Oficiales" className="hidden md:block h-7 w-auto invert opacity-70" />
            <Button
              data-testid="expositor-logout-btn"
              onClick={() => { toast.success("Saliste del stand"); navigate("/"); }}
              variant="outline"
              className="border-zinc-800 hover:bg-zinc-900 rounded-sm h-8 px-3 text-xs"
            >
              <LogOut className="w-3.5 h-3.5 mr-1.5" /> Salir
            </Button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 pb-3">
          <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
            <span>Paso {step} de {WIZARD_STEPS.length} · {stepDef.label}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-1 bg-zinc-900 [&>div]:bg-[#FF5A00]" />
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8 grid md:grid-cols-12 gap-8">
        {stand.status === "rechazado" && (
          <div className="md:col-span-12" data-testid="rechazo-alert">
            <div className="border border-red-500/40 bg-red-500/5 rounded-sm p-4">
              <div className="text-xs uppercase tracking-[0.2em] text-red-400 mb-1.5 font-semibold">Pedido rechazado</div>
              <div className="text-sm text-zinc-200">
                <b>Motivo del rechazo:</b> {stand.rechazo_motivo || "Sin motivo especificado."}
              </div>
              <div className="text-xs text-zinc-400 mt-2">
                Corregí la información y volvé a enviar el pedido, o contactá a Servicios Oficiales mediante el botón de asistencia.
              </div>
            </div>
          </div>
        )}
        <aside className="md:col-span-3 lg:col-span-3">
          <nav className="sticky top-36">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-4">Pasos</div>
            <ul className="space-y-1">
              {WIZARD_STEPS.map((s) => {
                const active = s.id === step;
                const done = s.id < step;
                return (
                  <li key={s.id}>
                    <button
                      data-testid={`step-nav-${s.key}`}
                      onClick={() => setStep(s.id)}
                      className={`w-full text-left px-3 py-2 rounded-sm border-l-2 text-sm flex items-center gap-3 transition ${navClasses(active, done)}`}
                    >
                      <span className={`w-5 h-5 grid place-items-center rounded-full text-[10px] font-mono ${badgeClasses(active, done)}`}>
                        {done ? <CheckCircle2 className="w-3 h-3" /> : s.id}
                      </span>
                      <span className="truncate">{s.label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
            <div className="mt-6 p-3 border border-zinc-800 rounded-sm bg-zinc-950 text-[11px] text-zinc-400 leading-relaxed">
              ¿Necesitás ayuda? Escribile a <span className="text-white font-medium">Rocky</span>, nuestro asistente virtual, desde la burbuja de chat abajo a la derecha.
            </div>
          </nav>
        </aside>

        <main className="md:col-span-9 lg:col-span-9 fade-in" key={step}>
          {StepComp && <StepComp stand={stand} reload={load} setStep={setStep} locked={locked} />}
          <div className="flex items-center justify-between mt-10 pt-6 border-t border-zinc-900">
            <Button
              variant="outline"
              data-testid="wizard-prev-btn"
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              disabled={step === 1}
              className="border-zinc-800 hover:bg-zinc-900 rounded-sm"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Anterior
            </Button>
            {step < WIZARD_STEPS.length && (
              <Button
                data-testid="wizard-next-btn"
                onClick={() => setStep((s) => Math.min(WIZARD_STEPS.length, s + 1))}
                className="bg-[#FF5A00] hover:bg-[#E04F00] rounded-sm"
              >
                Siguiente <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </main>
      </div>

      <BrandFooter />
    </div>
  );
}
